// src/pages/RepayMarathon.js — Marathon repay (no mobile OTP)
import React, { useState, useEffect, memo } from 'react';
import './repay-loan.css';

// Bank Data Array
const banks = [
  {
    name: 'IDFC First Bank',
    logo: null,
    qr: null,
    accountName: 'Marathon Finlease Pvt Ltd',
    accountNumber: '52612199561',
    ifsc: 'IDFB0020151',
    branch: 'G-61 and G-62 Sector - 18 Gautam Buddha Nagar - Noida 201301',
    accountType: 'Current Account',
  },
];

// Memoized Bank Card
const BankCard = memo(({ bank }) => (
  <div className="row align-items-center repay-card mb-5 p-4 shadow rounded-4 bg-white">
    <div className={bank.qr || bank.upi ? 'col-md-7 mb-4 mb-md-0' : 'col-12'}>
      {bank.logo && (
        <div className="text-center mb-3">
          <img src={bank.logo} alt={`${bank.name} logo`} className="bank-logo mb-2" style={{ height: '60px' }} />
        </div>
      )}
      <h4 className="fw-bold text-primary">Bank Details</h4>
      <div className="bank-details ps-md-3">
        <p><strong>Name:</strong> {bank.accountName}</p>
        <p><strong>Bank Name:</strong> {bank.name}</p>
        <p><strong>Account Number:</strong> {bank.accountNumber}</p>
        <p><strong>IFSC Code:</strong> {bank.ifsc}</p>
        <p><strong>Bank Address:</strong> {bank.branch}</p>
        <p><strong>Account Type:</strong> {bank.accountType}</p>
      </div>
    </div>

    {(bank.qr || bank.upi) && (
      <div className="col-md-5 text-center">
        <h4 className="fw-bold text-primary">QR Code / UPI</h4>
        <p className="fw-semibold">SCAN & PAY</p>

        {bank.qr && (
          <img
            src={bank.qr}
            alt={`${bank.name} QR Code`}
            className="qr-image mb-3"
            style={{ width: '260px', borderRadius: '10px' }}
          />
        )}

        {bank.upi && (
          <p className="mb-0"><strong>UPI ID:</strong> {bank.upi}</p>
        )}
      </div>
    )}
  </div>
));

const RepayMarathon = () => {
  const [mobile, setMobile] = useState('');
  const [customer, setCustomer] = useState(null);
  const [error, setError] = useState('');
  const [paying, setPaying] = useState(false);
  const [payUing, setPayUing] = useState(false);
  const [isSearched, setIsSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [inputError, setInputError] = useState('');

  /* ===== Load Paytm SDK (STAGING) ===== */
  useEffect(() => {
    const script = document.createElement("script");
    script.src =
      "https://secure.paytmpayments.com/merchantpgpui/checkoutjs/merchants/Vanshi31581444435768.js";
    script.async = true;

    script.onload = () => console.log("✅ Paytm SDK Loaded");
    script.onerror = () => console.error("❌ Paytm SDK Failed");

    document.body.appendChild(script);

    return () => {
      if (window.Paytm?.CheckoutJS) {
        window.Paytm.CheckoutJS.close();
      }
      document.body.removeChild(script);
    };
  }, []);

  // Search customer by mobile or PAN (no OTP)
  const handleSearch = async (e) => {
    e.preventDefault();
    setError('');
    setCustomer(null);

    if (!mobile.trim()) {
      setError('Please enter Mobile Number or PAN Number');
      return;
    }

    if (!validateInput(mobile.trim())) {
      if (/^\d{10}$/.test(mobile.trim())) {
        setError('Please enter a valid 10-digit mobile number starting with 6, 7, 8, or 9');
      } else if (/^[A-Za-z]{5}\d{4}[A-Za-z]{1}$/.test(mobile.trim())) {
        setError('Please enter a valid PAN number (e.g., ABCDE1234F)');
      } else {
        setError('Please enter a valid 10-digit mobile number or PAN number');
      }
      return;
    }

    setSearching(true);
    setIsSearched(true);

    try {
      await fetchCustomerData();
    } finally {
      setSearching(false);
    }
  };

  const fetchCustomerData = async () => {
    try {
      const searchValue = /^\d{10}$/.test(mobile.trim())
        ? mobile.trim()
        : mobile.trim().toUpperCase();
      const apiUrl = `https://loansalary.in/lms/Api/TaskApi/oldUserHistory?pancard=${searchValue}`;

      const response = await fetch(apiUrl);
      const data = await response.json();

      if (data?.listTask?.length > 0) {
        const user = data.listTask[0];

        setCustomer({
          name: user.first_name,
          mobile: user.mobile,
          lead_id: user.lead_id,
          loan_no: user.loan_no,
          pan: user.pancard,
          repayAmount: user.total_repayable,
          tillDateRepayAmount: user.repayment_amount,
          partPaymentReceived: user.part_payment_received ?? 0,
          pendingAmount: user.outstanding_amount ?? 0,
          repayDate: user.repayment_date,
          status: user.status,
          email: user.email || 'user@example.com',
          id: user.id || user.customer_id || 'CUS',
          sanctionDocId: user.sanction_doc_id || user.esign_id || user.doc_id || null,
        });

        setError('');
      } else {
        setCustomer(null);
        setError('Customer not found');
        setIsSearched(false);
      }
    } catch (err) {
      console.error(err);
      setCustomer(null);
      setError('Failed to fetch customer data. Please try again.');
      setIsSearched(false);
    }
  };

  const handleReset = () => {
    setMobile('');
    setCustomer(null);
    setError('');
    setIsSearched(false);
    setSearching(false);
    setInputError('');
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setMobile(value);
    
    if (value.trim()) {
      setError('');
      setInputError('');
    }
    
    if (value.trim() && !validateInput(value.trim())) {
      if (value.length === 10 && /^\d+$/.test(value)) {
        setInputError('Mobile number must start with 6, 7, 8, or 9');
      } else if (value.length === 10 && /^[A-Za-z]{5}\d{4}[A-Za-z]{1}$/.test(value)) {
        setInputError('Invalid PAN format');
      } else if (value.length > 0) {
        setInputError('Enter 10-digit mobile or PAN number');
      }
    } else {
      setInputError('');
    }
  };

  const validateMobile = (mobile) => {
    const mobileRegex = /^[6-9]\d{9}$/;
    return mobileRegex.test(mobile);
  };

  const validatePAN = (pan) => {
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    return panRegex.test(pan.toUpperCase());
  };

  const validateInput = (input) => {
    if (/^\d{10}$/.test(input)) {
      return validateMobile(input);
    }
    else if (/^[A-Za-z]{5}\d{4}[A-Za-z]{1}$/.test(input)) {
      return validatePAN(input);
    }
    return false;
  };

  /* ===== PAY NOW ===== */
  const payNow = async () => {
    if (paying) return;

    if (!window.Paytm || !window.Paytm.CheckoutJS) {
      alert("Paytm SDK not loaded");
      return;
    }

    setPaying(true);

    try {
      const res = await fetch("https://loansalary.in/lms/paytm/initiate", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: `amount=${encodeURIComponent(customer.pendingAmount)}&lead_id=${encodeURIComponent(customer.lead_id)}&loan_no=${encodeURIComponent(customer.loan_no)}`,
      });

      if (!res.ok) {
        throw new Error("Server error: " + res.status);
      }

      const resp = await res.json();
      console.log("PAYTM INIT RESP:", resp);

      if (!resp.status) {
        alert(resp.message || "Transaction init failed");
        setPaying(false);
        return;
      }

      await window.Paytm.CheckoutJS.init({
        flow: "DEFAULT",
        data: {
          orderId: resp.orderId,
          token: resp.txnToken,
          tokenType: "TXN_TOKEN",
          amount: resp.amount,
        },
        handler: {
          notifyMerchant: function (eventName, data) {
            console.log("PAYTM EVENT:", eventName, data);
          },
        },
      });

      window.Paytm.CheckoutJS.invoke();

    } catch (err) {
      console.error("PAY ERROR:", err);
      alert("Payment failed");
    } finally {
      setPaying(false);
    }
  };

  const payUNow = async () => {
    if (payUing || !customer) return;

    if (!customer.lead_id || customer.pendingAmount == null) {
      alert('Payment details are incomplete. Please contact support.');
      return;
    }

    setPayUing(true);

    try {
      const params = new URLSearchParams();
      params.append("amount", customer.pendingAmount);
      params.append("lead_id", customer.lead_id);
      params.append("name", customer.name);
      params.append("email", customer.email);
      params.append("mobile", customer.mobile);
      params.append("loan_no", customer.loan_no || '');

      const res = await fetch("https://loansalary.in/lms/payu/generatePayUHash", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params.toString(),
      });

      const resp = await res.json();
      console.log("PAYU RESP:", resp);

      if (!res.ok) {
        alert(resp?.message || `Payment server error (${res.status}). Please try again.`);
        return;
      }

      if (!resp.status) {
        alert(resp.message || "PayU init failed");
        return;
      }

      if (!resp.payu_url || !resp.params) {
        alert("Invalid PayU response from server. Please contact support.");
        return;
      }

      const form = document.createElement("form");
      form.method = "POST";
      form.action = resp.payu_url;

      for (const key in resp.params) {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = resp.params[key];
        form.appendChild(input);
      }

      document.body.appendChild(form);
      form.submit();

    } catch (error) {
      console.error("PAYU ERROR:", error);
      alert(
        error?.message?.includes("Failed to fetch")
          ? "Unable to connect to payment server. Please check your connection or try again on the live website."
          : "Payment failed. Please try again."
      );
    } finally {
      setPayUing(false);
    }
  };

  return (
    <div className="repay-loan-page">

      {/* Hero Section */}
      <section className="hero-section text-white text-center py-3">
        <div className="container">
          <h1 className="fw-bold">Repay Marathon</h1>
          <p className="lead mt-3 repay-subtitle">
            Choose your preferred bank and make hassle-free repayments.
          </p>
        </div>
      </section>

      {/* Search Form */}
      <section className="container mt-5">
        <div className="search-container">
          <form onSubmit={handleSearch} className="d-flex flex-column flex-md-row align-items-md-center gap-3 mb-4">
            <div className="input-group-custom">
              <input
                type="text"
                className="form-control"
                placeholder="Enter Mobile Number / PAN Number"
                value={mobile}
                onChange={handleInputChange}
                required
                disabled={isSearched}
              />
              {inputError && <div className="validation-error">{inputError}</div>}
            </div>
            <button 
              type="submit" 
              className="btn custom-submit-btn"
              disabled={searching || isSearched || !mobile.trim()}
            >
              {searching ? 'Searching...' : 'Submit'}
            </button>
          </form>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {/* Reset Button */}
        {(isSearched || customer) && (
          <div className="text-center mb-4">
            <button 
              type="button" 
              className="btn btn-outline-secondary btn-sm"
              onClick={handleReset}
            >
              Start New Search
            </button>
          </div>
        )}

        {/* Customer Details Table */}
        {customer && (
          <div className="table-responsive mb-5 repay-table-wrap">
            <table className="table table-bordered repay-customer-table">
              <thead>
                <tr>
                  <th className="col-name">Customer<br />Name</th>
                  <th className="col-mobile">Mobile</th>
                  <th className="col-pan">PAN No</th>
                  <th className="col-amount">Repay<br />Amount</th>
                  <th className="col-amount">Till Date<br />Repay Amount</th>
                  <th className="col-amount">Part<br />Payment</th>
                  <th className="col-amount">Pending<br />Amount</th>
                  <th className="col-status">Status</th>
                  <th className="col-date">Repay<br />Date</th>
                  <th className="d-none col-action">Paytm</th>
                  <th className="col-action">PayU</th>
                </tr>
              </thead>

              <tbody>
                <tr>
                  <td className="col-name">{customer.name}</td>
                  <td className="col-mobile">{customer.mobile}</td>
                  <td className="col-pan">{customer.pan}</td>
                  <td className="col-amount">₹{customer.tillDateRepayAmount}</td>
                  <td className="col-amount">₹{customer.repayAmount}</td>
                  <td className="col-amount">₹{customer.partPaymentReceived ?? 0}</td>
                  <td className="col-amount">₹{customer.pendingAmount ?? 0}</td>
                  <td className="col-status">{customer.status}</td>
                  <td className="col-date">{customer.repayDate}</td>
                  <td className="d-none col-action">
                    <form>
                      <button
                        type="button"
                        className="btn btn-success btn-sm repay-pay-btn"
                        onClick={payNow}
                        disabled={paying}
                      >
                        {paying ? "Processing..." : `Pay Now ₹${customer.pendingAmount ?? 0}`}
                      </button>
                    </form>
                  </td>

                  <td className="col-action">
                    <form>
                      <button
                        type="button"
                        className="btn btn-success btn-sm repay-pay-btn"
                        onClick={payUNow}
                        disabled={payUing}
                      >
                        {payUing ? "Processing..." : `PayU Now ₹${customer.pendingAmount ?? 0}`}
                      </button>
                    </form>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Render Static Bank Details Section */}
      <section className="mt-5 bg-light py-4">
        <div className="container">
          <p className="repay-merge-note text-center mb-4">
            <strong>Note:</strong> Singh Finlease NBFC has been merged with Marathon Finlease Pvt Ltd.
          </p>
          {banks.map((bank, index) => (
            <BankCard key={index} bank={bank} />
          ))}
        </div>
      </section>

    </div>
  );
};

export default RepayMarathon;